/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { VMInformation, VMDocTecnica, VMPlansAndProcedures, VMRecords, VMReview, VMEmergencyPlan } from '../custom';
import MobileMenu from '../custom/MobileMenu';
import { useAuthContext } from '../../context';
import { filtrarMenuPorPermissao } from '../../utils/menuPermissions';
import { NivelAcesso } from '../../services/api/users/UserServices';

enum Sections {
  INFORMACOES = 'informacoes',
  TECNICA = 'documentacao_tecnica',
  ACOMPANHAMENTOS = 'acompanhamentos',
  REGISTROS = 'registros',
  REVISAO = 'revisao',
  PLANO = 'PAE',
}

type FilteredPermissions = NivelAcesso;

type SectionData = {
  key: Sections;
  label: string;
  content: React.ReactNode;
};

interface IVMenuProps {
  id: string | undefined;
}

export const VMenu: React.FC<IVMenuProps> = ({ id }) => {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<Sections | null>(null);
  const [activeSection, setActiveSection] = useState<Sections | null>(null);

  const currentId = id || '';

  // console.log('usuário', userData);

  const fullMenuMap: NivelAcesso = useMemo(() => ({
    [Sections.INFORMACOES]: {
      central: '',
      identificacao: '',
      caracterizacao: '',
      organograma: '',
    },
    [Sections.TECNICA]: {
      caracteristicas: '',
      arquivos: '',
    },
    [Sections.ACOMPANHAMENTOS]: {
      comunicacao: '',
      visualizacao: '',
      inspecoes: '',
      anomalias: '',
    },
    [Sections.REGISTROS]: {
      RISR: '',
      RISROT: '',
      RISE: '',
      operacoes: '',
      monitoramento: '',
    },
    [Sections.REVISAO]: {
      RPSB: '',
    },
    [Sections.PLANO]: {
      PAE: '',
      conformidades: '',
      treinamento: '',
      seminario: '',
      simulado: '',
      registro: '',
    },
  }), []);


  const filteredPermissions: FilteredPermissions = useMemo(() => {
    const permissoesDoBackend = userData?.nivel_acesso || {};

    return filtrarMenuPorPermissao(fullMenuMap, permissoesDoBackend) as FilteredPermissions;
  }, [userData, fullMenuMap]);

  const getCurrentSectionFromUrl = (pathname: string): Sections | null => {
    if (pathname.includes('/entrepreneu_id') ||
      pathname.includes('/features-undertaking') ||
      pathname.includes('/organization_chart')) {
      return Sections.INFORMACOES;
    }
    if (pathname.includes('/technical_characteristics_project') ||
      pathname.includes('/files/file') ||
      pathname.includes('/files/foto')) {
      return Sections.TECNICA;
    }
    if (pathname.includes('/maintenance_planning') ||
      pathname.includes('/monitoring_instrumentation_plan') ||
      pathname.includes('/action_plan') ||
      pathname.includes('/dam_safety_inspections_planning')) {
      return Sections.ACOMPANHAMENTOS;
    }
    if (pathname.includes('/risr') ||
      pathname.includes('/rise') ||
      pathname.includes('/operation_records') ||
      pathname.includes('/instrumentation_geotechnical_records')) {
      return Sections.REGISTROS;
    }
    if (pathname.includes('/rpsb')) {
      return Sections.REVISAO;
    }
    if (pathname.includes('/emergency_plan') ||
      pathname.includes('/semiannual_internal_training') ||
      pathname.includes('/annual_guidance_seminar') ||
      pathname.includes('/emergency_situation_simulation') ||
      pathname.includes('/emergency_situation_register')) {
      return Sections.PLANO;
    }
    return null;
  };

  useEffect(() => {
    const currentSection = getCurrentSectionFromUrl(location.pathname);
    if (currentSection) {
      if (currentSection in filteredPermissions) {
        setActiveSection(currentSection);
        setOpenSubmenu(null);
      } else {
        setActiveSection(null);
        setOpenSubmenu(null);
      }
    }
  }, [location.pathname, filteredPermissions]);

  const sectionsData: SectionData[] = useMemo(() => {
    const data: SectionData[] = [];

    if (filteredPermissions[Sections.INFORMACOES]) {
      data.push({
        key: Sections.INFORMACOES,
        label: 'Informações',
        content: <VMInformation id={currentId} />,
      });
    }
    if (filteredPermissions[Sections.TECNICA]) {
      data.push({
        key: Sections.TECNICA,
        label: 'DOC. TÉCNICA',
        content: <VMDocTecnica id={currentId} />,
      });
    }
    if (filteredPermissions[Sections.REGISTROS]) {
      data.push({
        key: Sections.REGISTROS,
        label: 'Registros',
        content: <VMRecords id={currentId} />,
      });
    }
    if (filteredPermissions[Sections.ACOMPANHAMENTOS]) {
      data.push({
        key: Sections.ACOMPANHAMENTOS,
        label: 'Acompanhamentos',
        content: <VMPlansAndProcedures id={currentId} />,
      });
    }
    if (filteredPermissions[Sections.REVISAO]) {
      data.push({
        key: Sections.REVISAO,
        label: 'Revisão',
        content: <VMReview id={currentId} />
      });
    }
    if (filteredPermissions[Sections.PLANO]) {
      data.push({
        key: Sections.PLANO,
        label: 'PAE',
        content: <VMEmergencyPlan id={currentId} />,
      });
    }
    return data;
  }, [currentId, filteredPermissions]);

  const menus = useMemo(() => {
    const dynamicMenus = [];

    if (filteredPermissions[Sections.INFORMACOES]) {
      const infoButtons = [];
      if (typeof filteredPermissions[Sections.INFORMACOES]?.identificacao === 'string' && filteredPermissions[Sections.INFORMACOES].identificacao !== '') {
        infoButtons.push({ name: 'Identificação do empreendedor', route: `/information_dam/${currentId}/entrepreneu_id` });
      }
      if (typeof filteredPermissions[Sections.INFORMACOES]?.caracterizacao === 'string' && filteredPermissions[Sections.INFORMACOES].caracterizacao !== '') {
        infoButtons.push({ name: 'Caracterização da barragem', route: `/information_dam/${currentId}/features-undertaking` });
      }
      if (typeof filteredPermissions[Sections.INFORMACOES]?.organograma === 'string' && filteredPermissions[Sections.INFORMACOES].organograma !== '') {
        infoButtons.push({ name: 'Organograma geotecnia', route: `/information_dam/${currentId}/organization_chart` });
      }
      if (infoButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'Informações', buttons: infoButtons } });
      }
    }

    if (filteredPermissions[Sections.TECNICA]) {
      const docTecnicaButtons = [];
      if (typeof filteredPermissions[Sections.TECNICA]?.caracteristicas === 'string' && filteredPermissions[Sections.TECNICA].caracteristicas !== '') {
        docTecnicaButtons.push({ name: 'Características técnicas', route: `/information_dam/${currentId}/technical_characteristics_project` });
      }
      if (typeof filteredPermissions[Sections.TECNICA]?.arquivos === 'string' && filteredPermissions[Sections.TECNICA].arquivos !== '') {
        docTecnicaButtons.push({ name: 'Arquivos da Barragem', route: `/information_dam/${currentId}/files/file` });
      }
      if (docTecnicaButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'Doc. Técnica', buttons: docTecnicaButtons } });
      }
    }

    if (filteredPermissions[Sections.ACOMPANHAMENTOS]) {
      const acompanhamentosButtons = [];
      if (typeof filteredPermissions[Sections.ACOMPANHAMENTOS]?.comunicacao === 'string' && filteredPermissions[Sections.ACOMPANHAMENTOS].comunicacao !== '') {
        acompanhamentosButtons.push({ name: 'Calendário', route: `/information_dam/${currentId}/maintenance_planning` });
      }
      if (typeof filteredPermissions[Sections.ACOMPANHAMENTOS]?.visualizacao === 'string' && filteredPermissions[Sections.ACOMPANHAMENTOS].visualizacao !== '') {
        acompanhamentosButtons.push({ name: 'Plano de monitoramento/instrumentação', route: `/information_dam/${currentId}/monitoring_instrumentation_plan` });
      }
      if (typeof filteredPermissions[Sections.ACOMPANHAMENTOS]?.inspecoes === 'string' && filteredPermissions[Sections.ACOMPANHAMENTOS].inspecoes !== '') {
        acompanhamentosButtons.push({ name: 'Planejamento das inspeções de segurança da barragem', route: `/information_dam/${currentId}/dam_safety_inspections_planning` });
      }
      if (typeof filteredPermissions[Sections.ACOMPANHAMENTOS]?.anomalias === 'string' && filteredPermissions[Sections.ACOMPANHAMENTOS].anomalias !== '') {
        acompanhamentosButtons.push({ name: 'Plano de ação', route: `/action_plan/${currentId}/plans` });
      }
      if (acompanhamentosButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'Acompanhamentos', buttons: acompanhamentosButtons } });
      }
    }

    if (filteredPermissions[Sections.REGISTROS]) {
      const registrosButtons = [];
      if (typeof filteredPermissions[Sections.REGISTROS]?.RISR === 'string' && filteredPermissions[Sections.REGISTROS].RISR !== '') {
        registrosButtons.push({ name: 'Relatórios de Inspeção de Segurança Regular (RISR)', route: `/information_dam/${currentId}/risr` });
      }
      if (typeof filteredPermissions[Sections.REGISTROS]?.RISE === 'string' && filteredPermissions[Sections.REGISTROS].RISE !== '') {
        registrosButtons.push({ name: 'Relatório Inspeção de Segurança Especial (RISE)', route: `/information_dam/${currentId}/rise` });
      }
      if (typeof filteredPermissions[Sections.REGISTROS]?.operacoes === 'string' && filteredPermissions[Sections.REGISTROS].operacoes !== '') {
        registrosButtons.push({ name: 'Registros de Operação', route: `/information_dam/${currentId}/operation_records` });
      }
      if (typeof filteredPermissions[Sections.REGISTROS]?.monitoramento === 'string' && filteredPermissions[Sections.REGISTROS].monitoramento !== '') {
        registrosButtons.push({ name: 'Registros de monitoramento geotécnico e instrumentação', route: `/information_dam/${currentId}/instrumentation_geotechnical_records` });
      }
      if (typeof filteredPermissions[Sections.REGISTROS]?.RISROT === 'string' && filteredPermissions[Sections.REGISTROS].RISROT !== '') {
        registrosButtons.push({ name: 'Relatórios de Inspeção de Rotina (RISROT)', route: `/information_dam/${currentId}/risrot` });
      }
      if (registrosButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'Registros', buttons: registrosButtons } });
      }
    }

    if (filteredPermissions[Sections.REVISAO]) {
      const revisaoButtons = [];
      if (typeof filteredPermissions[Sections.REVISAO]?.RPSB === 'string' && filteredPermissions[Sections.REVISAO].RPSB !== '') {
        revisaoButtons.push({ name: 'Revisão Periódica de Segurança de Barragens (RPSB)', route: `/information_dam/${currentId}/rpsb` });
      }
      if (revisaoButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'Revisão', buttons: revisaoButtons } });
      }
    }

    if (filteredPermissions[Sections.PLANO]) {
      const paeButtons = [];
      if (typeof filteredPermissions[Sections.PLANO]?.PAE === 'string' && filteredPermissions[Sections.PLANO].PAE !== '') {
        paeButtons.push({ name: 'Plano de ação de emergência', route: `/information_dam/${currentId}/emergency_plan` });
      }
      if (typeof filteredPermissions[Sections.PLANO]?.treinamento === 'string' && filteredPermissions[Sections.PLANO].treinamento !== '') {
        paeButtons.push({ name: 'Treinamento Interno Semestral', route: `/information_dam/${currentId}/semiannual_internal_training` });
      }
      if (typeof filteredPermissions[Sections.PLANO]?.seminario === 'string' && filteredPermissions[Sections.PLANO].seminario !== '') {
        paeButtons.push({ name: 'Seminário Orientativo Anual', route: `/information_dam/${currentId}/annual_guidance_seminar` });
      }
      if (typeof filteredPermissions[Sections.PLANO]?.simulado === 'string' && filteredPermissions[Sections.PLANO].simulado !== '') {
        paeButtons.push({ name: 'Simulado de Situação de Emergência', route: `/information_dam/${currentId}/emergency_situation_simulation` });
      }
      if (typeof filteredPermissions[Sections.PLANO]?.registro === 'string' && filteredPermissions[Sections.PLANO].registro !== '') {
        paeButtons.push({ name: 'Registro de Situação de Emergências', route: `/information_dam/${currentId}/emergency_situation_register` });
      }
      if (paeButtons.length > 0) {
        dynamicMenus.push({ subMenu: { title: 'PAE', buttons: paeButtons } });
      }
    }

    return dynamicMenus;
  }, [currentId, filteredPermissions]);


  const handleButtonClick = (section: Sections) => {
    if (section in filteredPermissions) {
      if (openSubmenu === section) {
        setOpenSubmenu(null);
      } else {
        setOpenSubmenu(section);
        setActiveSection(section);
      }
    } else {
      console.warn(`Acesso negado para a seção: ${section}`);
    }
  };


  return (
    <>
      {smDown ? (
        <Box
          alignItems='center'
          display='flex'
          padding={1}
          justifyContent={'center'}
        >
          <MobileMenu menus={menus} />
        </Box>
      ) : (
        <>
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={2}
            alignItems={'center'}
            justifyContent={'center'}
            padding={1}
          >
            {sectionsData.map((sectionData) => (
              <React.Fragment key={sectionData.key}>
                <Button
                  variant={
                    (activeSection === sectionData.key || openSubmenu === sectionData.key)
                      ? 'contained'
                      : 'outlined'
                  }
                  fullWidth={smDown}
                  onClick={() => handleButtonClick(sectionData.key)}
                  size='small'
                  sx={{ borderRadius: '25px' }}
                >
                  <Typography variant='button' whiteSpace='nowrap' textOverflow='ellipsis' overflow='hidden'>
                    {sectionData.label}
                  </Typography>
                </Button>
                {smDown && openSubmenu === sectionData.key && (
                  <Box>
                    {sectionData.content}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
          {!smDown && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              alignContent={'center'}
            >
              {sectionsData.map((sectionData) => (
                <React.Fragment key={sectionData.key}>
                  {openSubmenu === sectionData.key && (
                    <Box
                      component={Paper}
                      variant='outlined'
                      padding={1}
                      width={'100%'}
                      sx={{ borderRadius: '25px' }}
                    >
                      {sectionData.content}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </>
      )}
    </>
  );
};